import { useMemo } from "react";
import { extractAirlineCarriers } from "@/app/lib/utils/extractAirlineCarriers";
import { OrderLabel } from "./my-bookings/OrderLabel";
import { sumDuration } from "../lib/utils/duration";
import type {
  FindUserReservationsExternalFlightReservation,
  FindUserReservationsFlightReservation,
  FindUserReservationsFlightReservationSegment,
} from "../lib/types/codegen";
import { SingleFlightInfo } from "./SingleFlightInfo";
import {
  SingleAvailableFlight,
  type Props as SingleAvailableFlightProps,
} from "./SingleAvailableFlight";

const SingleFlightReservationInfo = ({
  segments,
}: {
  segments: FindUserReservationsFlightReservationSegment[];
}) => {
  const firstSegment = segments[0];
  const lastSegment = segments[segments.length - 1];

  const stops = segments.filter((segment) => segment.stopTime).length;

  const duration = sumDuration(
    segments.flatMap((segment) => [segment.stopTime, segment.flightDuration]),
  );

  return (
    <SingleFlightInfo
      origin={firstSegment.origin}
      destination={lastSegment.destination}
      duration={duration}
      stops={stops}
      operatedBy={
        //  does operating carrier differ from marketing carrier
        firstSegment.operatingCarrier &&
        firstSegment.marketingCarrier &&
        firstSegment.operatingCarrier.code !==
          firstSegment.marketingCarrier.code
          ? firstSegment.operatingCarrier.name ||
            firstSegment.operatingCarrier.code
          : undefined
      }
    />
  );
};

type Props = Pick<SingleAvailableFlightProps, "bottomInfo" | "onClick"> & {
  itinerary: FindUserReservationsExternalFlightReservation;
  reservationStatus?: FindUserReservationsFlightReservation["status"];
};

export function SingleAvailableFlightReservation({
  itinerary,
  reservationStatus,
  bottomInfo,
  onClick,
}: Props) {
  const allAirlines = extractAirlineCarriers(itinerary.segments);

  const { departureFlight, returnFlight } = useMemo(() => {
    return itinerary.segments.reduce(
      (acc, segment, index) => {
        // start treating segments as return flight when we reach the first segment that has no stop time (i.e. is not a stop)
        if (
          acc.returnFlight.length === 0 &&
          (index === 0 || segment.stopTime)
        ) {
          acc.departureFlight.push(segment);
        } else {
          acc.returnFlight.push(segment);
        }

        return acc;
      },
      { departureFlight: [], returnFlight: [] } as {
        departureFlight: FindUserReservationsFlightReservationSegment[];
        returnFlight: FindUserReservationsFlightReservationSegment[];
      },
    );
  }, [itinerary.segments]);

  return (
    <SingleAvailableFlight
      onClick={onClick}
      airlines={allAirlines}
      topInfo={
        reservationStatus ? <OrderLabel status={reservationStatus} /> : null
      }
      bottomInfo={bottomInfo}
    >
      <SingleFlightReservationInfo segments={departureFlight} />
      {returnFlight.length > 0 && (
        <SingleFlightReservationInfo segments={returnFlight} />
      )}
    </SingleAvailableFlight>
  );
}
